<!-- components/modals/DeleteDocumentModalContent.vue -->
<script setup lang="ts">
import type { Component } from 'vue'
import { FileWaveIcon, FilePdfIcon, FileWordIcon, FileExcleIcon, ImageIcon } from '@/icons/'
import type { MediaDocument } from '@/types/media'

defineProps<{
  document: MediaDocument | null
}>()

const emit = defineEmits<{
  (e: 'confirm'): void
}>()

const getFileIcon = (fileType: string): Component => {
  const type = fileType?.toLowerCase() || ''
  if (type.includes('pdf')) return FilePdfIcon
  if (type.includes('word') || type.includes('doc') || type.includes('msword') || type.includes('document')) return FileWordIcon
  if (type.includes('excel') || type.includes('sheet') || type.includes('xls') || type.includes('spreadsheet') || type.includes('ms-excel')) return FileExcleIcon
  if (type.includes('jpg') || type.includes('jpeg') || type.includes('png') || type.includes('gif') || type.includes('bmp') || type.includes('image')) return ImageIcon
  return FileWaveIcon
}

const formatFileSize = (size: string | number): string => {
  const bytes = typeof size === 'string' ? parseInt(size, 10) : size
  if (!bytes || isNaN(bytes)) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const submitForm = () => {
  emit('confirm')
  return true
}

defineExpose({
  submitForm
})
</script>

<template>
  <div class="space-y-4">
    <div class="bg-red-50 border-l-4 border-red-400 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-red-700">
            <span class="font-medium">Warning:</span> Are you sure you want to delete this document? This action cannot be undone.
          </p>
        </div>
      </div>
    </div>

    <div v-if="document" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Document Details:</h3>
      <div class="space-y-3">
        <div class="flex items-center gap-3">
          <component :is="getFileIcon(document.fileType)" class="w-10 h-10 text-gray-600" />
          <div class="flex-1">
            <p class="font-medium text-gray-900">{{ document.title }}</p>
          </div>
        </div>
        <div class="text-sm">
          <span class="text-gray-600">Size:</span>
          <span class="font-medium ml-2">{{ formatFileSize(document.fileSize) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
